import { addCadFile } from '@api/projects.js';
import IconButton from '@components/Common/IconButton.js';
import toast from '@components/Common/Toast.js';
import { PlusIcon } from '@icons/IconLibrary.js';
import { COLUMN_EVENT_TYPES, columnUpdate } from '@signals/project/index.js';
import ProjectsState from '@states/Projects.js';
import { MemberState } from '@states/Viewer.js';
import { UIDiv, UIText } from '@ui';
import { getVisibleColumns } from '@utils/columnUtils.js';
import CheckboxCell from './cells/CheckboxCell.js';
import DateCell from './cells/DateCell.js';
import LinkCell from './cells/LinkCell.js';
import NumberCell from './cells/NumberCell.js';
import PeopleCell from './cells/PeopleCell.js';
import SelectCell from './cells/SelectCell.js';
import TextCell from './cells/TextCell.js';

export default class CreateTaskRow extends UIDiv {
  constructor(currentFolderId) {
    super();
    this.currentFolderId = currentFolderId;
    this.isCreating = false;
    this.isExpanded = false;
    this.columnInputs = new Map(); // Store input references for value extraction
    this.columnValues = new Map(); // Store the actual values
    this.visibleColumns = getVisibleColumns(this.currentFolderId);

    this.addClass('flex w-full h-12 min-w-fit border-b border-gray-200');

    columnUpdate.add(data => {
      this.#handleColumnChange(data);
    });

    this.#render();
  }

  /**
   * Handles keyboard events for the component.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();

      if (this.isExpanded && this.createButton) {
        this.#createTask();
      }
    }
  }

  #render() {
    this.clear();
    if (this.isExpanded) {
      this.#renderExpandedView();
    } else {
      this.#renderCollapsedView();
    }
  }

  #renderCollapsedView() {
    const container = new UIDiv();
    container.setClass(
      'flex h-full w-full items-center gap-2 px-2 relative cursor-text',
    );
    const icon = new PlusIcon({
      size: 16,
      color: 'none',
      strokeColor: null,
      className:
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--o-hive-primary-80)]',
    });

    const textElement = new UIText(i18next.t('pages.projects.addTask'));
    icon.setAttribute('tabindex', '0');
    icon.onKeyDown(e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.#expand();
      }
    });
    container.onClick(e => {
      e.stopPropagation();
      this.#expand();
    });
    container.add(icon, textElement);

    this.add(container);
  }

  #renderExpandedView() {
    // Create cells for each visible column
    this.visibleColumns.forEach(column => {
      // Skip people column if not in organization context
      const { rootFolderOrganizationId } = ProjectsState.projects;
      if (!rootFolderOrganizationId && column.type === 'people') {
        return;
      }

      const cell = this.#createColumnCell(column);
      this.add(cell);
    });

    // Add spacer cell to fill remaining space
    const spacerCell = new UIDiv();
    spacerCell.addClass('flex-1 border-b border-gray-200');
    spacerCell.setStyle('min-width', ['0']);
    spacerCell.setStyle('max-width', ['none']);
    this.add(spacerCell);

    // Add actions cell with create and cancel buttons
    const actionsCell = this.#createActionsCell();
    this.add(actionsCell);
  }

  #createColumnCell(column) {
    // Create appropriate cell component based on column type
    let cellComponent;

    // check if we have the value
    const existingValue = this.columnValues.get(column.id) || '';

    // Create a fake item for the cell components
    const fakeItem = { id: 'new-task', reconstructions: [] };

    // Create value change callback
    const onValueChange = (newValue, event) => {
      this.columnValues.set(column.id, newValue);
      if (event) this.#handleKeyDown(event);
    };

    switch (column.type) {
      case 'text':
        cellComponent = new TextCell({
          column: column,
          item: fakeItem,
          value: existingValue,
          canEdit: true,
          isTask: true,
          onValueChange: onValueChange,
        });
        break;
      case 'date':
        cellComponent = new DateCell({
          column: column,
          item: null,
          value: existingValue,
          canEdit: true,
          onValueChange: onValueChange,
        });
        break;
      case 'select':
        cellComponent = new SelectCell({
          column: column,
          item: null,
          value: existingValue,
          canEdit: true,
          currentFolderId: this.currentFolderId,
          onValueChange: onValueChange,
        });
        break;
      case 'number':
        cellComponent = new NumberCell(
          column,
          null,
          existingValue,
          true,
          onValueChange,
        );
        break;
      case 'people':
        cellComponent = new PeopleCell({
          column: column,
          item: null,
          value: existingValue,
          canEdit: true,
          onValueChange: onValueChange,
          members: MemberState.members,
        });
        break;
      case 'checkbox':
        cellComponent = new CheckboxCell(
          column,
          null,
          existingValue,
          true,
          onValueChange,
        );
        break;
      case 'link':
        cellComponent = new LinkCell(
          column,
          null,
          existingValue,
          true,
          onValueChange,
        );
        break;
      default:
        cellComponent = new UIDiv();
        cellComponent.add(new UIText('N/A'));
    }

    cellComponent.addClass(
      'p-2 flex-shrink-0 border-b border-r border-gray-200',
    );

    // Set width constraints
    const width = column.width || '150px';
    cellComponent.setStyle('width', [width]);

    // Store reference to the cell component
    this.columnInputs.set(column.id, cellComponent);

    return cellComponent;
  }

  #createActionsCell() {
    const cell = new UIDiv();
    cell.addClass(
      'border-b border-l border-gray-200 sticky right-0 flex-shrink-0 bg-white flex items-center justify-center gap-2 px-2 z-10',
    );
    cell.setStyle('width', ['70px']);

    // Create button
    this.createButton = new IconButton({
      iconName: 'plus',
      iconSize: 16,
      buttonClass:
        '!filter-none bg-[var(--o-hive-primary-80)] text-white hover:bg-[var(--o-hive-primary-100)]',
      onClick: () => this.#createTask(),
    });

    cell.add(this.createButton);
    return cell;
  }

  #expand() {
    if (!this.isExpanded) {
      this.isExpanded = true;
      this.#render();
      // focus on the first input cell
      const firstColumn = this.visibleColumns[0];
      if (firstColumn) {
        const firstInput = this.columnInputs.get(firstColumn.id);
        if (firstInput) {
          firstInput.enterEditMode();
        }
      }
    }
  }

  #collapse() {
    if (this.isExpanded) {
      this.isExpanded = false;
      this.columnInputs.clear();
      this.columnValues.clear();
      this.#render();
    }
  }

  async #createTask() {
    // Get task name from the first visible column
    let taskName = '';
    const firstColumn = this.visibleColumns[0];
    if (firstColumn) {
      taskName = this.columnValues.get(firstColumn.id) || '';
    }

    if (!taskName || !taskName.trim()) {
      toast.warning(i18next.t('pages.projects.taskNameIsRequired'));
      return;
    }

    if (this.isCreating) {
      return;
    }

    this.isCreating = true;

    try {
      // Collect column values from the columnValues map
      const columnValues = [];

      this.visibleColumns.forEach(column => {
        const value = this.columnValues.get(column.id);

        if (value !== null && value !== undefined && value !== '') {
          columnValues.push({
            columnId: column.id,
            value: value,
          });
        }
      });

      // Create the task using the API
      const payload = {
        name: taskName.trim(),
        modelFolderId: this.currentFolderId,
        reconstructions: [],
        columnValues: columnValues,
      };

      const response = await addCadFile(payload);

      if (response.status === 'success') {
        // Create new task object for state
        const newTask = {
          id: response.response.cadFileID,
          name: taskName.trim(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          thumbnail: null,
          columnValues: columnValues,
          parentId: this.currentFolderId,
          accessType: 'edit',
          sharingId: response.response.sharingId || null,
          uid: response.response.uid || null,
          organizationId: response.response.organizationId || null,
          cadfileAccessLevel: response.response.cadfileAccessLevel || null,
          commentsEnabled: false,
          reconstructions: [],
          rowOrder: null,
        };

        // Update the ProjectsState
        ProjectsState.setProjects({
          ...ProjectsState.projects,
          tasks: [...ProjectsState.projects.tasks, newTask],
        });

        // Reset the form
        this.#collapse();
        toast.success(i18next.t('pages.projects.taskCreatedSuccessfully'));
      } else {
        throw new Error(i18next.t('pages.projects.failedToCreateTask'));
      }
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(i18next.t('pages.projects.failedToCreateTask'));
    } finally {
      this.isCreating = false;
    }
  }

  #handleColumnChange(data) {
    if (data.currentFolderId === this.currentFolderId) {
      this.visibleColumns = getVisibleColumns(this.currentFolderId);
      switch (data.type) {
        case COLUMN_EVENT_TYPES.VISIBILITY_CHANGED:
        case COLUMN_EVENT_TYPES.DELETED:
        case COLUMN_EVENT_TYPES.RESET:
        case COLUMN_EVENT_TYPES.ORDERING:
        case COLUMN_EVENT_TYPES.ADDED:
          if (this.isExpanded) this.#render();
          break;

        default:
          break;
      }
    }
  }
}
